// v4 setup diagnostics: programmatic setup report, loud warnings for unsafe setups,
// and the double-bundle guard. Sources use the shared vocabulary
// explicit | inferred | missing | ambiguous (see BIRDSEYE.md).

import { PF_VERSION, PRESETS } from './constants.js';
import { normalizeText } from './utils.js';
import { describeOption } from './controls.js';

export function warnIfOtherBundle(win) {
  var existing = win.PandaFilter;
  if (!existing) {
    return;
  }

  var existingVersion = existing.version || 'unknown';
  console.warn(
    '[PandaFilter] Another Panda Filter bundle (version ' + existingVersion + ') is already ' +
    'loaded on this page; this bundle is ' + PF_VERSION + '. Use exactly one Panda Filter ' +
    'bundle per page — running v3 and v4 together is not supported. Remove one script tag.'
  );
}

// Prototype method: structured setup report for the current instance.
export var getDiagnostics = function () {
  var discovery = this.discovery || { list: {}, item: {}, empty: {} };
  var warnings = [];

  var itemFieldNames = {};
  this.items.forEach(function (record) {
    Object.keys(record.fields || {}).forEach(function (name) {
      itemFieldNames[name] = true;
    });
  });
  var fieldNames = Object.keys(itemFieldNames);
  var mapKeys = Object.keys(this.fieldMap || {});

  var controlFields = [];
  this.filterControls.forEach(function (control) {
    var field = normalizeText(control.getAttribute('data-pf-filter') || '');
    if (field && controlFields.indexOf(field) === -1) {
      controlFields.push(field);
    }
  });

  var missingControlFields = controlFields.filter(function (field) {
    return !itemFieldNames[field];
  });

  var fieldsSource = fieldNames.length
    ? 'explicit'
    : mapKeys.length
      ? 'map'
      : controlFields.length
        ? 'missing'
        : 'none-needed';

  if (this.items.length && missingControlFields.length) {
    warnings.push(
      'Filter controls reference field(s) [' + missingControlFields.join(', ') + '] that no ' +
      'item provides. Add [data-pf-field] elements inside items, provide data-pf-map, ' +
      'or run assisted setup (Webflow MCP).'
    );
  }

  // Same-field trap: items carry the field, yet not one option value matches any item. The
  // options and the item data are bound to different CMS fields (Name label vs Slug item
  // data): every check above is green, and the filter still matches nothing.
  var self = this;
  var optionsByField = {};
  this.filterControls.forEach(function (control) {
    var option = describeOption(control);
    if (option) {
      (optionsByField[option.field] = optionsByField[option.field] || []).push(option);
    }
  });
  // Per-field option report: which option values match at least one loaded item. Options
  // matching none are is-empty candidates; ALL of them matching none is the trap below.
  var optionReport = {};
  Object.keys(optionsByField).forEach(function (field) {
    var withItems = 0;
    var withoutItems = [];
    optionsByField[field].forEach(function (option) {
      if (self._optionHasItems(option)) {
        withItems += 1;
      } else if (withoutItems.indexOf(option.value) === -1) {
        withoutItems.push(option.value);
      }
    });
    optionReport[field] = {
      total: optionsByField[field].length,
      withItems: withItems,
      withoutItems: withoutItems,
    };
  });
  Object.keys(optionsByField).forEach(function (field) {
    var options = optionsByField[field];
    if (!itemFieldNames[field] || optionReport[field].withItems > 0) {
      return;
    }
    var itemValue = '';
    self.items.some(function (record) {
      itemValue = (record.fields[field] || [])[0] || '';
      return !!itemValue;
    });
    warnings.push(
      'Filter field "' + field + '": none of its ' + options.length + ' option(s) matches any item ' +
      '(option "' + options[0].value + '" vs item value "' + itemValue + '"). The options and the item ' +
      'data are likely bound to different CMS fields (e.g. Name vs Slug) — bind both to the same one.'
    );
  });

  // Webflow does not publish .w-dyn-empty for a list that has items, so a filtered list
  // without data-pf-empty just goes blank when nothing matches.
  if (!this.emptyNode && (this.filterControls.length || this.searchControls.length)) {
    warnings.push(
      'No empty state: add a hidden "No results" block with data-pf-empty inside the wrapper ' +
      '(Webflow omits .w-dyn-empty from published lists that have items).'
    );
  }

  // A typo'd preset silently falls back to plain defaults (e.g. no load=all), which looks
  // like a working setup until the collection outgrows one CMS page.
  if (this.presetName && !Object.prototype.hasOwnProperty.call(PRESETS, this.presetName)) {
    warnings.push(
      'Unknown data-pf-preset "' + this.presetName + '" — ignored, plain defaults apply. ' +
      'Known presets: ' + Object.keys(PRESETS).join(', ') + '.'
    );
  }

  // Note: an inferred list/items is the intended wrapper-only flow, not a problem —
  // it is reported via `source` (and debug mode), not warned about on every load.
  return {
    version: PF_VERSION,
    instanceKey: this.instanceKey,
    wrapper: { found: true },
    list: { source: discovery.list.source || 'explicit' },
    item: {
      source: discovery.item.source || 'explicit',
      selector: this.itemSelector,
      count: this.items.length,
    },
    fields: {
      source: fieldsSource,
      names: fieldNames,
      controlFields: controlFields,
      missingForControls: missingControlFields,
      options: optionReport,
    },
    dataset: {
      complete: this.datasetComplete,
      loadedPages: this.loadedPageUrls.size,
      pendingPages: this.pendingPageUrls.length,
      failedPages: this.failedPageUrls.size,
    },
    controls: {
      filters: this.filterControls.length,
      search: this.searchControls.length,
      reset: this.resetControls.length,
      filtersWrapper: !!this.filtersWrapper,
      source: this.filterControls.length || this.searchControls.length ? 'explicit' : 'missing',
    },
    preset: this.presetName || null,
    pagination: { source: this.paginationSource },
    empty: { source: discovery.empty.source || (this.emptyNode ? 'explicit' : 'missing') },
    warnings: warnings,
  };
};

// Prototype method: surface setup problems once at init — loud, but not fatal.
export var _reportSetupDiagnostics = function () {
  var report = this.getDiagnostics();

  report.warnings.forEach(function (warning) {
    console.warn('[PandaFilter][' + report.instanceKey + '] ' + warning);
  });

  this._debugLog('setup:diagnostics', report);
  return report;
};
